import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../../data/content";
import { ArrowDown } from "lucide-react";

const LINES = ["INDUSTRIAL", "PACKAGING", "ENGINEERED"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.4 + i * 0.12 }
  })
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-background"
    >
      {/* Background image with parallax */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={IMAGES.heroCrate}
          alt="Heavy-duty export crate"
          className="w-full h-full object-cover object-center opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-transparent to-transparent" />
      </motion.div>

      {/* Hero content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 min-h-screen flex flex-col pt-24 md:pt-32 pb-16"
      >
        {/* Top meta bar */}
        <div className="flex justify-between items-center border-t border-border/60 pt-4 gap-4">
          <span className="font-mono-ibm text-[10px] md:text-xs tracking-widest text-muted-foreground">
            EST. BENGALURU · INDIA
          </span>
          <span className="font-mono-ibm text-[10px] md:text-xs tracking-widest text-muted-foreground text-right">
            EXPORT-GRADE · ISPM-15
          </span>
        </div>

        {/* Main hero block */}
        <div className="flex-1 flex flex-col justify-center pt-10">
        <div className="max-w-5xl">
          {LINES.map((line, i) => (
            <span key={line} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block font-display uppercase leading-[0.86] tracking-tighter text-[16vw] md:text-[11vw] lg:text-[10rem] ${
                  i === 2 ? "text-primary" : "text-foreground"
                }`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.76, 0, 0.24, 1] }}
          className="mt-10 max-w-xl border-l-2 border-primary pl-6"
        >
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Heavy-duty corrugated, plywood, pinewood & precision foam systems —
            engineered for air and sea freight, protecting aerospace, defence,
            electrical and industrial cargo with pan-India delivery and export
            to any port on earth.
          </p>
        </motion.div>

        <motion.button
          data-testid="hero-cta"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.3 }}
          onClick={() =>
            document.querySelector("#products")?.scrollIntoView({ behavior: "smooth" })
          }
          className="mt-10 inline-flex items-center gap-3 font-mono-ibm text-xs tracking-widest uppercase group w-fit"
        >
          <span className="bg-primary text-primary-foreground px-6 py-4 group-hover:bg-foreground group-hover:text-background transition-colors">
            View Capabilities
          </span>
          <span className="w-12 h-12 border border-border flex items-center justify-center group-hover:border-primary transition-colors">
            <ArrowDown size={16} className="group-hover:text-primary transition-colors" />
          </span>
        </motion.button>
        </div>
      </motion.div>
    </section>
  );
};
